import React, { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { listApps } from "@/lib/api";
import AppCard from "@/components/AppCard";
import AdSlot from "@/components/AdSlot";
import { DIRECTORY } from "@/constants/testIds";
import { REGION_KEYS } from "@/i18n/translations";

const AUDIENCES = ["owners", "riders", "trainers", "breeders", "barns", "vets"];

const AppsPage = () => {
  const { t } = useLang();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    region: "",
    audience: "",
    sort: "score",
  });

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.region) params.region = filters.region;
    if (filters.audience) params.audience = filters.audience;
    if (filters.sort) params.sort = filters.sort;
    const id = setTimeout(() => {
      listApps(params)
        .then(setApps)
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(id);
  }, [filters]);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <main className="hac-container py-12 md:py-16">
      <div className="mb-10">
        <div className="overline mb-3 text-[#2563EB]">{t.nav.directory}</div>
        <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-tight">
          {t.hero.title}
        </h1>
      </div>

      <AdSlot placement="directory-banner" />

      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 md:p-6 mb-10 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-5 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => update("search", e.target.value)}
            placeholder={t.common.searchPlaceholder}
            data-testid={DIRECTORY.search}
            className="hac-input pl-9"
          />
        </div>
        <select
          value={filters.region}
          onChange={(e) => update("region", e.target.value)}
          data-testid={DIRECTORY.region}
          className="hac-input md:col-span-2"
        >
          <option value="">{t.common.regions}</option>
          {t.regions.map((r) => (
            <option key={r} value={REGION_KEYS[r] || r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={filters.audience}
          onChange={(e) => update("audience", e.target.value)}
          data-testid={DIRECTORY.audience}
          className="hac-input md:col-span-3"
        >
          <option value="">{t.common.audience}</option>
          {AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {t.audiences[a]}
            </option>
          ))}
        </select>
        <select
          value={filters.sort}
          onChange={(e) => update("sort", e.target.value)}
          data-testid={DIRECTORY.sort}
          className="hac-input md:col-span-2"
        >
          <option value="score">{t.common.sortScore}</option>
          <option value="rating">{t.common.sortRating}</option>
          <option value="name">{t.common.sortName}</option>
        </select>
      </div>

      <div className="flex items-center justify-between mb-6 text-sm text-[#64748B]">
        <span className="font-mono">
          {apps.length} {t.common.ofApps}
        </span>
        <span className="flex items-center gap-2">
          <SlidersHorizontal size={14} /> {t.common.filters}
        </span>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#94A3B8]">…</div>
      ) : apps.length === 0 ? (
        <div className="text-center py-20 text-[#94A3B8]">{t.common.noResults}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((a) => (
            <AppCard key={a.slug} app={a} />
          ))}
        </div>
      )}
    </main>
  );
};

export default AppsPage;
